<template>
  <section class="container ccpa-container">
    <div class="row">
      <div v-if="success" class="banner banner--small banner--green col s12 l6 offset-l3">
        <p class="banner__text banner__text--left">You have opted out on {{ date() }}</p>
      </div>
      <div v-if="errorBanner" class="banner banner--small banner--red col s12 l6 offset-l3">
        <p class="banner__text banner__text--left">There was an error in updating your preference. Please try again.</p>
      </div>
      <form class="form-card col s12 l6 offset-l3" @submit.prevent="submit">
        <h1 class="ccpa-card col s12 heading-3 xs-vertical-spacing">Do Not Sell My Personal Information</h1>
        <p class="col s12 body-2 xs-vertical-spacing">
          At Sittercity, we honor the importance of personal information. That's why:
        </p>
        <ul class="col s11 offset-s1 l11 offset-l1 list-group sm-vertical-spacing">
          <li class="list-group__item list-group--bullet body-2">We're not selling any personal information</li>
          <li class="list-group__item list-group--bullet body-2">We don't have plans to sell personal information</li>
        </ul>
        <p class="col s12 body-2 sm-vertical-spacing">
          For more information, read our <a href="https://www.sittercity.com/privacy"><u>Privacy Policy</u></a> or this
          <a href="https://support.sittercity.com/hc/en-us/articles/360040666454-Does-Sittercity-sell-my-personal-information"
            ><u>Help Center article</u></a
          >
          about personal data. We respect and understand that you may wish to ensure that your personal information isn't sold in
          the future.
        </p>
        <p class="col s12 body-2 sm-vertical-spacing">If so, please update your status below.</p>
        <p class="col s12 body-2 lg-vertical-spacing">
          If you're an existing Sittercity member, login before completing this form.
        </p>

        <div class="col s12 m4 l3">
          <poppins-button
            value="Log In"
            :href="'/login?return_path=%2Fdo-not-sell-my-personal-information'"
            variant="outlined"
            size="medium"
            class="md-vertical-spacing"
          />
        </div>

        <div class="col s12">
          <div class="col s12 divider md-vertical-spacing"></div>
        </div>

        <div class="col s12 m6 md-vertical-spacing">
          <poppins-text-field
            id="first_name"
            v-model="form.first_name"
            labelName="First name"
            name="first_name"
            type="text"
            class="mb-16"
            :status="formStatuses.first_name"
            :helperText="helperText(formStatuses.first_name, 'first_name', form.first_name)"
            @blur="validateRequiredFields('first_name', form.first_name)"
          />
        </div>

        <div class="col s12 m6 md-vertical-spacing">
          <poppins-text-field
            id="last_name"
            v-model="form.last_name"
            labelName="Last name"
            name="last_name"
            type="text"
            class="mb-16"
            :status="formStatuses.last_name"
            :helperText="helperText(formStatuses.last_name, 'last_name', form.last_name)"
            @blur="validateRequiredFields('last_name', form.last_name)"
          />
        </div>

        <div class="col s12">
          <div class="col s12 divider md-vertical-spacing"></div>
        </div>

        <div class="col s12 md-vertical-spacing">
          <poppins-text-field
            id="Address"
            v-model="form.address"
            labelName="Address"
            name="Address"
            type="text"
            class="mb-24"
            aria-label="Address"
            :status="formStatuses.address"
            :helperText="helperText(formStatuses.address, 'address', form.address)"
            @blur="validateRequiredFields('address', form.address)"
          />
        </div>

        <div class="col s12 l6 md-vertical-spacing">
          <poppins-text-field
            id="City"
            v-model="form.city"
            labelName="City name"
            name="City"
            type="text"
            class="mb-24"
            aria-label="City"
            :status="formStatuses.city"
            :helperText="helperText(formStatuses.city, 'city', form.city)"
            @blur="validateRequiredFields('city', form.city)"
          />
        </div>

        <div class="col s12 m6 l3 lg-vertical-spacing">
          <poppins-dropdown
            id="state"
            v-model="form.state"
            labelName="State"
            name="address-level2"
            class="mb-16"
            :options="states"
            :status="formStatuses.state"
            :helperText="helperText(formStatuses.state, 'state', form.state)"
            @blur="validateRequiredFields('state', form.state)"
          />
        </div>

        <div class="col s12 m6 l3 md-vertical-spacing">
          <poppins-text-field
            id="zipcode"
            v-model="form.zipcode"
            labelName="ZIP code"
            name="postal-code"
            type="text"
            maxlength="5"
            pattern="[0-9]*"
            class="mb-24"
            inputmode="numeric"
            aria-label="Five-digit zip code"
            :status="formStatuses.zipcode"
            :helperText="helperText(formStatuses.zipcode, 'zipcode', form.zipcode)"
            :limit-input-to-pattern="true"
            @blur="validateZipcode"
          />
        </div>

        <div class="col s12 md-vertical-spacing">
          <poppins-text-field
            id="email"
            v-model="form.email"
            labelName="Email"
            name="email"
            type="email"
            class="mb-16"
            autocomplete="email"
            :status="formStatuses.email"
            :helperText="helperText(formStatuses.email, 'email', form.email)"
            @blur="validateEmail"
          />
        </div>

        <div class="col s12 md-vertical-spacing">
          <poppins-text-field
            id="phone"
            v-model="form.phone"
            labelName="Phone Number"
            name="Phone"
            type="text"
            class="mb-24"
            aria-label="Phone"
            :status="formStatuses.phone"
            :helperText="helperText(formStatuses.phone, 'phone', form.phone)"
            @blur="validatePhone"
          />
        </div>

        <div class="col s12">
          <div class="col s12 divider md-vertical-spacing"></div>
        </div>

        <div class="col s12 lg-vertical-spacing">
          <poppins-checkbox
            v-model="form.opt_out"
            checkbox-id="form-not"
            labelName="Do Not Sell My Personal Information"
            labelClass="mb-4"
            :status="formStatuses.opt_out"
            :helperText="helperText(formStatuses.opt_out, 'opt_out', form.opt_out)"
            @blur="validateOptOut"
          />
        </div>

        <div class="col s12 m4 l3">
          <poppins-button value="submit" size="medium" class="md-vertical-spacing" />
        </div>
      </form>
    </div>
  </section>
</template>

<script lang="ts">
import helperStatus from '../../helpers/status';
import states from '../../helpers/states';
import { validateEmail } from '../../helpers/emailHelper';
import TextField from '@/components/textfield/TextField.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import Button from '@/components/button/Button.vue';
import { CREATE_NON_CUSTOMER_DSR_REQUEST } from '../../graphql/dsr/mutations';

export default {
  name: 'DoNotSellFormLoggedOut',
  components: {
    PoppinsDropdown: Dropdown,
    PoppinsTextField: TextField,
    PoppinsCheckbox: Checkbox,
    PoppinsButton: Button,
  },
  data(): any {
    return {
      states,
      form: {
        first_name: '',
        last_name: '',
        address: '',
        city: '',
        state: '',
        zipcode: '',
        email: '',
        phone: '',
        opt_out: false,
      },
      formStatuses: {
        first_name: helperStatus.Neutral,
        last_name: helperStatus.Neutral,
        address: helperStatus.Neutral,
        city: helperStatus.Neutral,
        email: helperStatus.Neutral,
        zipcode: helperStatus.Neutral,
        phone: helperStatus.Neutral,
        state: helperStatus.Neutral,
        opt_out: helperStatus.Neutral,
      },
      requiredFields: ['first_name', 'last_name', 'address', 'city', 'state', 'zipcode', 'email', 'phone', 'opt_out'],
      success: false,
      errorBanner: false,
    };
  },
  computed: {
    formattedPhone(): string {
      const digits = this.form.phone.replace(/\D+/g, '');
      switch (digits.length) {
        case 12:
          return digits.substring(2);
        case 11:
          return digits.substring(1);
        default:
          return digits;
      }
    },
  },
  watch: {
    form: {
      handler() {
        Object.keys(this.formStatuses).forEach((key) => {
          if (this.formStatuses[key] !== helperStatus.Error) {
            this.formStatuses[key] = helperStatus.Neutral;
          }
        });
      },
      deep: true,
    },
    'form.zipcode': function zipcodeHandler(newValue) {
      if (this.zipcodeIsValid(newValue)) {
        this.formStatuses.zipcode = helperStatus.Neutral;
      }
    },
    'form.address': function addressHandler(newValue) {
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.address = helperStatus.Neutral;
      }
    },
    'form.city': function cityHandler(newValue) {
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.city = helperStatus.Neutral;
      }
    },
    'form.last_name': function lastNameHandler(newValue) {
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.last_name = helperStatus.Neutral;
      }
    },
    'form.first_name': function firstNameHandler(newValue) {
      if (!this.fieldIsEmpty(newValue)) {
        this.formStatuses.first_name = helperStatus.Neutral;
      }
    },
    'form.phone': function phoneHandler(newValue) {
      if (this.phoneIsValid(newValue)) {
        this.formStatuses.phone = helperStatus.Neutral;
      }
    },
    'form.email': function emailHandler(newValue) {
      if (validateEmail(newValue)) {
        this.formStatuses.email = helperStatus.Neutral;
      }
    },
    'form.state': function stateHandler(newValue) {
      if (newValue) {
        this.formStatuses.state = helperStatus.Neutral;
      }
    },
    'form.opt_out': function optOutHandler(newValue) {
      if (newValue) {
        this.formStatuses.opt_out = helperStatus.Neutral;
      }
    },
  },
  methods: {
    date() {
      return new Date().toLocaleDateString('en-us', { month: 'long', day: 'numeric', year: 'numeric' });
    },
    validateRequiredFields(fieldName, fieldValue) {
      if (this.fieldIsRequired(fieldName) && this.fieldIsEmpty(fieldValue)) {
        this.formStatuses[fieldName] = helperStatus.Error;
      }
    },
    validateZipcode() {
      this.validateRequiredFields('zipcode', this.form.zipcode);
      if (!this.zipcodeIsValid(this.form.zipcode)) {
        this.formStatuses.zipcode = helperStatus.Error;
      }
    },
    validatePhone() {
      this.validateRequiredFields('phone', this.form.phone);
      if (!this.phoneIsValid(this.form.phone)) {
        this.formStatuses.phone = helperStatus.Error;
      }
    },
    validateEmail() {
      this.validateRequiredFields('email', this.form.email);
      if (!validateEmail(this.form.email)) {
        this.formStatuses.email = helperStatus.Error;
      }
    },
    validateOptOut() {
      if (this.form.opt_out === false) {
        this.formStatuses.opt_out = helperStatus.Error;
      }
    },
    fieldIsEmpty(fieldValue) {
      return fieldValue.length === 0;
    },
    fieldIsRequired(fieldName) {
      return this.requiredFields.includes(fieldName);
    },
    phoneIsValid(value) {
      const phoneRegex = /^(\+0?1\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
      return phoneRegex.test(value);
    },
    zipcodeIsValid(value) {
      const zipCodeRegex = /^\d{5}$/;
      return zipCodeRegex.test(value);
    },
    helperText(status: helperStatus, type: string, value: string) {
      if (status === helperStatus.Error) {
        if (type === 'zipcode' && value.length > 0) {
          return 'Please enter a five digit zip code';
        }
        if (type === 'email' && value.length > 0 && !validateEmail(value)) {
          return 'Enter a valid email';
        }
        if (type === 'phone' && value.length > 0 && !this.validatePhone(value)) {
          return 'Please enter a valid phone number';
        }
        if (this.requiredFields.includes(type)) {
          return 'This field is required';
        }
      }
      return '';
    },
    canSubmit() {
      const newErrors = {};

      this.validateOptOut();

      Object.entries(this.form).forEach((e) => {
        this.validateRequiredFields(e[0], e[1]);
      });

      Object.keys(this.formStatuses).forEach((key) => {
        if (this.formStatuses[key] === helperStatus.Error) {
          const helperText = 'This field is required';
          const status = helperStatus.Error;
          newErrors[key] = { helperText, status };
        }
      });

      this.errors = newErrors;
      if (Object.keys(newErrors).length > 0) {
        return false;
      }

      return true;
    },
    submit() {
      if (this.canSubmit()) return this.submitDoNotSellForm();
      return false;
    },
    async submitDoNotSellForm() {
      this.showLoader = true;
      try {
        await this.$apollo.mutate({
          mutation: CREATE_NON_CUSTOMER_DSR_REQUEST,
          variables: {
            firstName: this.form.first_name,
            lastName: this.form.last_name,
            address: this.form.address,
            city: this.form.city,
            state: this.form.state,
            zip: this.form.zipcode,
            email: this.form.email,
            phone: this.formattedPhone,
            optOut: this.form.opt_out,
          },
        });

        this.success = true;
        window.scrollTo(0, 0);
      } catch (_error) {
        this.errorBanner = true;
        window.scrollTo(0, 0);
      }
    },
  },
};
</script>

<style scoped>
.col {
  padding: 0 1rem;
}
</style>
